import React, { useState, useEffect, useRef } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock } from 'lucide-react';

interface DateTimePickerProps {
    value: string; // ISO string or empty
    onChange: (value: string) => void;
    label?: string;
    required?: boolean;
}

export const DateTimePicker: React.FC<DateTimePickerProps> = ({ value, onChange, label, required }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [viewDate, setViewDate] = useState(new Date()); // For calendar navigation
    const [selectedDate, setSelectedDate] = useState<Date | null>(value ? new Date(value) : null);
    const [time, setTime] = useState(value ? new Date(value).toTimeString().slice(0, 5) : '12:00');
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (value) {
            const date = new Date(value);
            setSelectedDate(date);
            setTime(date.toTimeString().slice(0, 5));
        }
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const daysInMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay(); // 0 = Sunday

    const handleDateClick = (day: number) => {
        const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
        setSelectedDate(newDate);
        updateParent(newDate, time);
    };

    const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newTime = e.target.value;
        setTime(newTime);
        if (selectedDate) {
            updateParent(selectedDate, newTime);
        }
    };

    const updateParent = (date: Date, timeStr: string) => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        const newDateTime = new Date(date);
        newDateTime.setHours(hours);
        newDateTime.setMinutes(minutes);
        // Format to YYYY-MM-DDTHH:MM local time
        const pad = (n: number) => n < 10 ? '0' + n : n;
        const localISO = `${newDateTime.getFullYear()}-${pad(newDateTime.getMonth() + 1)}-${pad(newDateTime.getDate())}T${pad(hours)}:${pad(minutes)}`;
        onChange(localISO);
    };

    const nextMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
    const prevMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));

    const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
    const days = ["Do", "Lu", "Ma", "Mi", "Ju", "Vi", "Sa"];

    // Generate calendar grid
    const totalDays = daysInMonth(viewDate);
    const startDay = firstDayOfMonth(viewDate);
    const calendarDays = [];
    for (let i = 0; i < startDay; i++) calendarDays.push(null);
    for (let i = 1; i <= totalDays; i++) calendarDays.push(i);

    const formatDateDisplay = (dateStr: string) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="relative" ref={containerRef}>
            {label && (
                <label className="text-xs font-bold text-gray-700 mb-2 block uppercase tracking-wide">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}

            <div
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full pl-4 pr-4 py-3 bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all cursor-pointer hover:bg-white hover:shadow-md font-medium flex items-center justify-between ${isOpen ? 'ring-2 ring-purple-500 bg-white' : ''}`}
            >
                <div className="flex items-center gap-3">
                    <CalendarIcon className="w-5 h-5 text-purple-500" />
                    <span className={value ? "text-gray-900" : "text-gray-400"}>
                        {value ? formatDateDisplay(value) : "Seleccionar fecha y hora..."}
                    </span>
                </div>
            </div>

            {isOpen && (
                <div className="absolute top-full left-0 z-50 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-100 p-4 transform transition-all animate-in fade-in zoom-in-95 duration-200">
                    <div className="flex items-center justify-between mb-4">
                        <button onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded-full text-gray-600 transition-colors"><ChevronLeft className="w-5 h-5" /></button>
                        <span className="font-bold text-gray-800">{monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}</span>
                        <button onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded-full text-gray-600 transition-colors"><ChevronRight className="w-5 h-5" /></button>
                    </div>

                    <div className="grid grid-cols-7 gap-1 mb-2 text-center">
                        {days.map(d => <span key={d} className="text-xs font-medium text-gray-400">{d}</span>)}
                    </div>

                    <div className="grid grid-cols-7 gap-1 mb-4">
                        {calendarDays.map((day, idx) => {
                            if (day === null) return <div key={`empty-${idx}`} />;
                            const isSelected = selectedDate && selectedDate.getDate() === day && selectedDate.getMonth() === viewDate.getMonth() && selectedDate.getFullYear() === viewDate.getFullYear();
                            const isToday = day === new Date().getDate() && viewDate.getMonth() === new Date().getMonth() && viewDate.getFullYear() === new Date().getFullYear();

                            return (
                                <button
                                    key={day}
                                    onClick={() => handleDateClick(day)}
                                    className={`
                                        h-8 w-8 rounded-full text-sm font-medium flex items-center justify-center transition-all
                                        ${isSelected ? 'bg-purple-600 text-white shadow-lg shadow-purple-200' : 'hover:bg-purple-50 text-gray-700'}
                                        ${isToday && !isSelected ? 'text-purple-600 border border-purple-200 bg-purple-50/50' : ''}
                                    `}
                                >
                                    {day}
                                </button>
                            );
                        })}
                    </div>

                    <div className="pt-4 border-t border-gray-100 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <label className="text-xs font-medium text-gray-500">Hora:</label>
                        <input
                            type="time"
                            value={time}
                            onChange={handleTimeChange}
                            className="flex-1 px-2 py-1 bg-gray-50 border border-gray-200 rounded-md text-sm outline-none focus:border-purple-500 transition-colors"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
